import { EditorState } from "prosemirror-state";
import type { Node as PMNode } from "prosemirror-model";
import type { Plugin } from "prosemirror-state";
import { nlsSchema } from "./schema.ts";
import type { Statement, FlatStatement, Link } from "../documents/types.ts";
import { flattenWithDepth } from "../documents/addressing.ts";

/**
 * Convert an NLS document tree to a ProseMirror document.
 * Flattens the tree: each statement becomes a top-level node with a `level` attr.
 */
export function documentToDoc(root: Statement): PMNode {
  const flat = flattenWithDepth(root);

  if (flat.length === 0) {
    // Empty document — create one empty statement
    return nlsSchema.node("doc", null, [
      nlsSchema.node("statement", { sid: "1", level: 1, collapsed: false }),
    ]);
  }

  const statements = flat.map(({ statement, depth }) => {
    const content = buildInlineContent(statement);
    return nlsSchema.node(
      "statement",
      {
        sid: statement.sid,
        level: depth,
        collapsed: false,
      },
      content
    );
  });

  return nlsSchema.node("doc", null, statements);
}

/**
 * Build inline content (text + link marks) for a statement.
 */
function buildInlineContent(statement: Statement): PMNode[] {
  if (!statement.content && statement.links.length === 0) {
    return [];
  }

  const nodes: PMNode[] = [];

  if (statement.content) {
    nodes.push(nlsSchema.text(statement.content));
  }

  // Append links as bracketed text with marks
  for (const link of statement.links) {
    const mark = nlsSchema.mark("nls_link", {
      documentId: link.target.documentId,
      sid: link.target.sid ?? null,
      viewspec: link.target.viewspec ?? null,
    });
    // Add a space before the link
    if (nodes.length > 0) {
      nodes.push(nlsSchema.text(" "));
    }
    nodes.push(nlsSchema.text(`[${link.label}]`, [mark]));
  }

  return nodes;
}

/**
 * Convert a ProseMirror document back to a flat statement list.
 */
export function docToFlatStatements(doc: PMNode): FlatStatement[] {
  const statements: FlatStatement[] = [];

  doc.forEach((node) => {
    if (node.type.name === "statement") {
      const links: Link[] = [];
      let content = "";

      // Extract text and links from inline content
      node.forEach((child) => {
        if (child.isText) {
          const linkMark = child.marks.find((m) => m.type.name === "nls_link");
          if (linkMark) {
            // This is link text — extract it
            const label = (child.text ?? "").replace(/^\[|\]$/g, "");
            links.push({
              id: crypto.randomUUID(),
              label,
              target: {
                documentId: linkMark.attrs.documentId as string,
                sid: linkMark.attrs.sid as string | undefined,
                viewspec: linkMark.attrs.viewspec as string | undefined,
              },
            });
          } else {
            content += child.text ?? "";
          }
        }
      });

      statements.push({
        id: crypto.randomUUID(),
        sid: node.attrs.sid as string,
        content: content.trim(),
        level: node.attrs.level as number,
        links,
      });
    }
  });

  return statements;
}

/**
 * Reconstruct a Statement tree from flat statements with levels.
 * Uses levels to determine parent-child relationships.
 */
export function flatStatementsToTree(
  flat: FlatStatement[],
  rootContent: string = ""
): Statement {
  const root: Statement = {
    id: crypto.randomUUID(),
    sid: "0",
    content: rootContent,
    children: [],
    links: [],
    metadata: {
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
  };

  // Stack of { statement, level }
  const stack: Array<{ stmt: Statement; level: number }> = [
    { stmt: root, level: 0 },
  ];

  for (const flat_stmt of flat) {
    const stmt: Statement = {
      id: flat_stmt.id,
      sid: flat_stmt.sid,
      content: flat_stmt.content,
      children: [],
      links: flat_stmt.links,
      metadata: {
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
      },
    };

    // Pop stack until we find the parent level
    while (stack.length > 1 && stack[stack.length - 1]!.level >= flat_stmt.level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1]!;
    parent.stmt.children.push(stmt);
    stack.push({ stmt, level: flat_stmt.level });
  }

  return root;
}

/**
 * Create a ProseMirror EditorState from an NLS document.
 */
export function createEditorState(
  root: Statement,
  plugins: Plugin[] = []
): EditorState {
  const doc = documentToDoc(root);
  return EditorState.create({
    doc,
    plugins,
  });
}
